import {
  ApplicationConfig,
  APP_INITIALIZER,
  ErrorHandler,
  provideEnvironmentInitializer,
  inject,
} from '@angular/core';
import {
  provideRouter,
  withEnabledBlockingInitialNavigation,
} from '@angular/router';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { NEVER, firstValueFrom, of } from 'rxjs';

import { routes } from './app.routes';
import { AuthService } from './core/services/auth.service';
import { BrandDomainInterceptor } from './core/interceptors/brand-domain.interceptor';
import { CredentialsInterceptor } from './core/interceptors/credentials-interceptor';
import { XsrfInterceptor } from './core/interceptors/xsrf-interceptor';
import { AuthInterceptor } from './core/services/auth.interceptor';
import { GateService } from './core/services/gate.service';
import { GlobalErrorHandler } from './core/logging/global-error-handler';
import { LoggingInterceptor } from './core/logging/logging.interceptor';
import { GclidService } from './core/services/gclid.service';
import { MarketingTagsService } from './core/services/marketing-tags.service';
import { UtmService } from './core/services/utm.service';
import { VanityRedirectService } from './core/services/vanity-redirect.service';
import { filter, switchMap, take } from 'rxjs/operators';
import { ImpersonationService } from './core/services/impersonation.service';

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes, withEnabledBlockingInitialNavigation()),
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimations(),

    // Marketing tags (prod only), then capture UTMs/gclid before gate redirect or router navigation.
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {
        inject(MarketingTagsService).load();
        inject(UtmService);
        inject(GclidService);
        return Promise.resolve();
      },
      multi: true,
    },

    // Gate check runs after attribution capture — blocks navigation until the cookie is validated.
    // If the cookie is missing the service redirects to /gate and the
    // promise never resolves, preventing the app from rendering.
    {
      provide: APP_INITIALIZER,
      // Waits for an impersonation handoff: the gate's redirect would otherwise cancel the redeem request.
      useFactory: (gate: GateService, impersonation: ImpersonationService) => () =>
        firstValueFrom(impersonation.handoffSettled$.pipe(switchMap(() => gate.checkAccess()))),
      deps: [GateService, ImpersonationService],
      multi: true,
    },

    // Auth bootstrap runs after the gate check passes.
    // An impersonation handoff is redeemed first, so the very first /me already sees the session.
    provideEnvironmentInitializer(() => {
      const auth = inject(AuthService);
      const impersonation = inject(ImpersonationService);
      impersonation.handoffSettled$.pipe(take(1)).subscribe(() => auth.bootstrap());
      impersonation.settleHandoffFromLocation();
    }),

    // Vanity canonical-host redirect after auth profile is loaded.
    {
      provide: APP_INITIALIZER,
      useFactory: (auth: AuthService, vanityRedirect: VanityRedirectService) => () =>
        firstValueFrom(
          auth.authReady$.pipe(
            filter((ready) => !!ready),
            take(1),
            // Hold the app back while a full-page redirect is under way (an impersonation transfer
            // takes a request first), so the wrong host never renders.
            switchMap(() =>
              auth.isAuthenticated && vanityRedirect.maybeRedirect(auth.currentUserProfile) ? NEVER : of(true),
            ),
          ),
        ),
      deps: [AuthService, VanityRedirectService],
      multi: true,
    },

    { provide: HTTP_INTERCEPTORS, useClass: BrandDomainInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: CredentialsInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: XsrfInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: LoggingInterceptor, multi: true },
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
  ],
};